import styled from 'styled-components';

/**
 * Provider profile left info sidebar.
 * Values map to the Figma "Side Sheet" frame (node 1187:21546) on the
 * "Provider Page UI Updates" page of the Axon Updates file.
 */
const AXON = '#6f42f5'; // axon/500 (Primary Purple)
const TEXT_PRIMARY = '#121212'; // text/primary
const TEXT_SECONDARY = 'rgba(18, 18, 18, 0.6)'; // text/secondary
const DIVIDER = 'rgba(18, 18, 18, 0.12)'; // divider
const SUCCESS = '#2e7d32'; // success/main (online dot, target met)
const ERROR = '#d32f2f'; // error/main (short of target)
const TRACK = 'rgba(18, 18, 18, 0.12)'; // progress track

export const SidebarWrap = styled.aside`
  box-sizing: border-box;
  width: 300px;
  flex-shrink: 0;
  align-self: stretch;
  background: #ffffff;
  border-right: 1px solid ${DIVIDER};
  padding: 40px 24px;
  display: flex;
  flex-direction: column;
  gap: 32px;

  /* ---- Card header: avatar + name + subtitle ---- */
  .card-header {
    display: flex;
    align-items: center;
    padding-top: 16px;
  }
  .avatar-wrap {
    position: relative;
    margin-right: 16px;
    flex-shrink: 0;
    width: 40px;
    height: 40px;
  }
  .avatar-wrap .avatar {
    width: 40px;
    height: 40px;
    border-radius: 100px;
    object-fit: cover;
    display: block;
    background: #eae8ff;
  }
  .avatar-wrap .status-dot {
    position: absolute;
    bottom: -2px;
    right: -2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .avatar-wrap .status-dot::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${SUCCESS};
  }
  .avatar-wrap.offline .status-dot::after {
    background: #9e9e9e;
  }
  .card-header .text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
  }
  .card-header .name {
    font-size: 16px;
    line-height: 1.5;
    letter-spacing: 0.15px;
    color: ${TEXT_PRIMARY};
    word-break: break-word;
  }
  .card-header .subtitle {
    font-size: 14px;
    line-height: 1.43;
    letter-spacing: 0.17px;
    color: ${TEXT_SECONDARY};
    word-break: break-word;
  }

  /* ---- Booked sessions / hours progress ---- */
  .booked {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .booked-row {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .booked-row .label {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.57;
    letter-spacing: 0.1px;
    color: ${TEXT_PRIMARY};
  }
  .booked-row .meter {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .booked-row .track {
    flex: 1;
    min-width: 0;
    height: 4px;
    border-radius: 4px;
    background: ${TRACK};
    overflow: hidden;
  }
  .booked-row .fill {
    height: 100%;
    border-radius: 4px;
    transition: width 0.3s ease;
  }
  /* Booked vs target, matching the planner's session badge: short of target is
     red, at or over target is green, no target set is neutral. Solid tones
     rather than the planner's pastel fills, which vanish in a 4px bar. */
  .booked-row .fill.under {
    background: ${ERROR};
  }
  .booked-row .fill.met {
    background: ${SUCCESS};
  }
  .booked-row .fill.none {
    background: ${TRACK};
  }
  .booked-row .limit-edit {
    border: none;
    background: none;
    padding: 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    color: ${AXON};
    font-size: 15px;
    line-height: 1;
  }
  /* Shared "reveal more detail" affordance — used by the hours breakdown and
     the caseload list, defined once so they always look the same. */
  .breakdown-toggle {
    border: none;
    background: none;
    padding: 0;
    cursor: pointer;
    color: ${AXON};
    font-size: 12px;
    font-weight: 500;
    text-decoration: underline;
    white-space: nowrap;
  }
  /* Day-by-day working, so the weekly figure can be checked against the grid. */
  .hours-breakdown {
    margin-top: 4px;
    padding: 8px 10px;
    border: 1px solid ${DIVIDER};
    border-radius: 6px;
  }
  .hours-breakdown table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    color: ${TEXT_PRIMARY};
  }
  .hours-breakdown th {
    text-align: right;
    font-weight: 500;
    color: ${TEXT_SECONDARY};
    padding-bottom: 4px;
  }
  .hours-breakdown th:first-child,
  .hours-breakdown td:first-child {
    text-align: left;
  }
  .hours-breakdown td {
    text-align: right;
    padding: 2px 0;
  }
  .hours-breakdown .total-row td {
    border-top: 1px solid ${DIVIDER};
    padding-top: 4px;
    font-weight: 700;
  }
  .hours-breakdown .breakdown-note {
    margin: 6px 0 0;
    font-size: 11px;
    line-height: 1.4;
    color: ${TEXT_SECONDARY};
  }
  .booked-row .pct {
    font-size: 14px;
    line-height: 1.43;
    letter-spacing: 0.17px;
    color: ${TEXT_SECONDARY};
    white-space: nowrap;
  }

  /* ---- Provider information block ---- */
  .info {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .info-head {
    display: flex;
    align-items: center;
    gap: 28px;
  }
  .info-head .info-title {
    flex: 1;
    min-width: 0;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.75;
    letter-spacing: 0.15px;
    color: ${TEXT_PRIMARY};
    margin: 0;
  }
  .info-head .edit {
    border: none;
    background: none;
    cursor: pointer;
    padding: 0 8px;
    height: 24px;
    color: ${AXON};
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.4px;
    text-transform: capitalize;
    border-radius: 4px;
  }
  .info-head .edit:hover {
    background: rgba(111, 66, 245, 0.08);
  }
  .info-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .info-field .field-label {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 14px;
    line-height: 1.43;
    letter-spacing: 0.17px;
    color: ${TEXT_SECONDARY};
  }
  .info-field .field-value {
    font-size: 14px;
    line-height: 1.43;
    letter-spacing: 0.17px;
    color: ${TEXT_PRIMARY};
    word-break: break-word;
  }
`;
